import {Injectable} from '@angular/core';
import {DropDownGroupBy, DropDownGroupedBy, DropDownItem} from "../../models/general/dropdownItem.model";
import {TranslateService} from "@ngx-translate/core";
import {FindResourceDropdownItem} from "../../main/pm/resources/models/find-resource-dropdown-item";
import {WorkflowDropdownItem, WorkflowDropdownItemEntity} from "../../models/dropdowns/workflow-dropdown-item";

// Group key for the "Robert" section. groupDropdownOptions sorts groups by KEY with
// localeCompare('en', {sensitivity:'base', numeric:true}) (dropdown-helpers.ts:27-29) and digits collate before
// letters, so '0robert' always lands ABOVE 'active'/'inactive'/'matched'/'unmatched'. A plain 'robert' would
// sort BELOW 'active' — do not "tidy" this string.
export const ROBERT_GROUP_KEY = '0robert';

@Injectable({
    providedIn: 'root'
})
export class PredefinedDropdownGroupService {

    constructor(
        private translateService: TranslateService
    ) {
    }

    dropdownGroupByActive(): DropDownGroupBy {
        return {
            status: true,
            toggle: false,
            hasMultiple: false,
            toggleLabelAsync: this.translateService.stream('tp-show-inactive', {defaultText: 'Show inactive'}),
            group: (item: DropDownItem) => {
                if (item.isActive == true) return 'active';
                else if (item.isActive == false) return 'inactive';
                else return 'unknown';
            },
            label: (group: DropDownGroupedBy, groups: DropDownGroupedBy[]) => {
                const activeLabel = this.translateService.stream('tp-active', {defaultText: 'Active'});
                const inactiveLabel = this.translateService.stream('tp-inactive', {defaultText: 'Inactive'});
                switch (group.group) {
                    case 'active':
                        return {display: groups.length > 1, textAsync: activeLabel};
                    case 'inactive':
                        return {display: true, textAsync: inactiveLabel};
                }
            },
            display: function (group: DropDownGroupedBy) {
                switch (group.group) {
                    case 'inactive':
                        return this.toggle;
                    default:
                        return true;
                }
            }
        };
    }

    dropdownGroupByRate(): DropDownGroupBy {
        return {
            status: true,
            toggle: true,
            hasMultiple: false,
            toggleLabelAsync: this.translateService.stream('tp-show-unmatched', {defaultText: 'Show unmatched'}),
            group: (item: FindResourceDropdownItem) => {
                if (item.rate != null) return 'matched';
                else if (item.rate == null) return 'unmatched';
                else return 'unknown';
            },
            label: (group: DropDownGroupedBy, groups: DropDownGroupedBy[]) => {
                const matchedLabel = this.translateService.stream('tp-matched', {defaultText: 'Matched'});
                const unmatchedLabel = this.translateService.stream('tp-unmatched', {defaultText: 'Unmatched'});
                switch (group.group) {
                    case 'matched':
                        return {display: groups.length > 1, textAsync: matchedLabel};
                    case 'unmatched':
                        return {display: true, textAsync: unmatchedLabel};
                }
            },
            display: function (group: DropDownGroupedBy) {
                switch (group.group) {
                    case 'unmatched':
                        return this.toggle;
                    default:
                        return true;
                }
            }
        };
    }
    dropdownGroupByWorkflow(): DropDownGroupBy {
        return {
            status: true,
            toggle: true,
            hasMultiple: false,
            toggleLabelAsync: this.translateService.stream('tp-show-steps', {defaultText: 'Show steps'}),
            group: (item: WorkflowDropdownItem) => {
                if (item.entity === WorkflowDropdownItemEntity.Workflow) return WorkflowDropdownItemEntity.Workflow;
                else if (item.entity === WorkflowDropdownItemEntity.WorkflowStep) return WorkflowDropdownItemEntity.WorkflowStep;
                else if (item.entity === WorkflowDropdownItemEntity.RobertWorkflow) return WorkflowDropdownItemEntity.RobertWorkflow;
                else return 'unknown';
            },
            label: (group: DropDownGroupedBy, groups: DropDownGroupedBy[]) => {
                const matchedLabel = this.translateService.stream('tp-associated-workflows', {defaultText: 'Associated workflows'});
                const unmatchedLabel = this.translateService.stream('tp-workflow-steps', {defaultText: 'Workflow Steps'});
                const robertLabel = this.translateService.stream('tp-robert-workflows', {defaultText: 'Robert workflows'});
                switch (group.group) {
                    case WorkflowDropdownItemEntity.Workflow:
                        return {display: groups.length > 1, textAsync: matchedLabel};
                    case WorkflowDropdownItemEntity.WorkflowStep:
                        return {display: true, textAsync: unmatchedLabel};
                    case WorkflowDropdownItemEntity.RobertWorkflow:
                        return {display: true, textAsync: robertLabel};
                }
            },
            display: function (group: DropDownGroupedBy) {
                switch (group.group) {
                    case WorkflowDropdownItemEntity.WorkflowStep:
                        return this.toggle;
                    default:
                        return true;
                }
            }
        };
    }

    /**
     * Wraps an existing grouping with a "Robert" section pinned to the top of the panel, holding the options
     * flagged `isRobertMapped` (stamped by stampRobertOptions()). Used by the client / resource / language
     * dropdowns so a PM can see at a glance which rows are mapped in Robert.
     *
     * PARTITION, not duplication — groupDropdownOptions puts each option in exactly ONE bucket, so a flagged row
     * MOVES here instead of being listed twice. That is forced, not stylistic: mat-select's _propagateChanges
     * emits `this.selected.map(o => o.value)` over option INSTANCES, so two rows sharing an id on a [multiple]
     * control write [id, id] into the FormControl, and _setSelectionByValue never re-propagates to clean it up —
     * on the target-languages picker that produces two client jobs for one language.
     *
     * `base` must be the grouping the dropdown would use otherwise (dropdownGroupByActive() for the implicit
     * default, dropdownGroupByRate() for the resource picker). ONE WRAPPER PER DROPDOWN — tp-dropdown writes
     * `hasMultiple` onto this object and two-way-binds `toggle` to it, so sharing one instance across two
     * dropdowns cross-talks their "Show inactive" toggles.
     *
     * When nothing carries the flag (Robert off, or no mappings) every callback delegates straight to `base` and
     * the rendered grouping is identical to today's.
     */
    withRobertSection(base: DropDownGroupBy): DropDownGroupBy {
        const robertLabel = this.translateService.stream('tp-robert', {defaultText: 'Robert'});
        const wrapper: any = {
            ...base,                       // status / toggle / toggleLabelAsync carried over verbatim
            _baseKeys: new Set<string>(),  // base keys seen during the last grouping pass — drives hasMultiple
            _hasMultiple: false,

            // `function`, never an arrow: groupDropdownOptions invokes these as methods ON the groupBy object,
            // which is how the base implementations read `this.toggle`.
            group: function (item: DropDownItem, options?: DropDownItem[]): string {
                const baseKey = base.group(item, options);
                this._baseKeys.add(baseKey);
                if (!item?.isRobertMapped) return baseKey;
                // Only promote a row whose normal section is CURRENTLY DISPLAYED. Without this, a Robert-mapped
                // but inactive client (or an unmatched resource) would surface in the always-visible Robert
                // section while "Show inactive" / "Show unmatched" is off — a data-visibility regression, not a
                // cosmetic one. Re-evaluated on every toggle flip via onActiveChange() -> filterOptions().
                return base.display.call(this, {group: baseKey, options: []}) ? ROBERT_GROUP_KEY : baseKey;
            },
            label: function (group: DropDownGroupedBy, groups?: DropDownGroupedBy[]) {
                if (group.group === ROBERT_GROUP_KEY) return {display: true, textAsync: robertLabel};
                // The Robert group MUST count towards the base's own "is there more than one group?" test. The
                // bases hide their primary header ("Active" / "Matched") while it is the only displayed group,
                // and groupDropdownOptions renders headers as flat sibling rows — so suppressing it here would
                // leave the "Robert" header unclosed and the ordinary rows below it would read as Robert rows.
                // Same reason dropdownGroupByWorkflow lets its own Robert group count.
                return base.label(group, groups ?? []);
            },
            display: function (group: DropDownGroupedBy, groups?: DropDownGroupedBy[]): boolean {
                if (group.group === ROBERT_GROUP_KEY) return true;
                return base.display.call(this, group, groups);
            }
        };
        // tp-dropdown writes hasMultiple from the grouping of the FULL option list, and that flag alone decides
        // whether the "Show inactive" / "Show unmatched" toggle chip renders. Recompute it from the BASE keys so
        // the Robert section can never make that chip appear where it doesn't today — it matters: on a freshly
        // opened resource-job modal every resource is 'unmatched' (one group, no chip), and a chip appearing
        // there is one click away from hiding every non-Robert resource.
        // The setter runs exactly once per filterOptions(), after both grouping passes, so the key set is
        // complete when it is read; it clears itself for the next cycle.
        Object.defineProperty(wrapper, 'hasMultiple', {
            get(): boolean { return this._hasMultiple; },
            set(value: boolean) { this._hasMultiple = value && this._baseKeys.size > 1; this._baseKeys.clear(); },
            enumerable: true, configurable: true
        });
        return wrapper as DropDownGroupBy;
    }
}
